import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Reveal, SectionHead, Panel, Tag } from "@/components/System";
import { ECONOMY_VERBS, LINKS } from "@/data/content";

const PILLARS = [
    ["IDENTITY", "Every economic actor must be someone — even a machine. Onchain identity makes agents attributable."],
    ["PERMISSIONS", "Policy-controlled wallets and scoped permissions bound what an agent is allowed to do with value."],
    ["EXECUTION", "Protected execution ensures the work that justifies payment actually happened as specified."],
    ["EVIDENCE", "Evidence bundles let payers verify deliverables before settlement completes."],
    ["PAYMENTS", "USDC-denominated value movement gives agents predictable, dollar-stable transaction costs."],
    ["SETTLEMENT", "Deterministic finality on Arc closes the loop: machine-speed commerce with verifiable outcomes."],
];

const Economy = () => (
    <div>
        <Seo path="/economy" />
        <section className="relative overflow-hidden px-4 pb-16 pt-36 sm:px-6">
            <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl">
                <div className="vm-micro mb-6">AGENTVM://ECONOMY</div>
                <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-[#F5F9FF] sm:text-6xl">
                    WHEN SOFTWARE
                    <br />
                    <span className="text-[#00D9FF] vm-glow">BECOMES ECONOMIC.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg">
                    Agents can move beyond producing information. With identity, proof, and settlement working
                    together, they can participate in economic activity directly.
                </p>
            </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
            <div className="grid gap-px border border-[rgba(0,217,255,0.12)] bg-[rgba(0,217,255,0.12)] sm:grid-cols-2 lg:grid-cols-3">
                {ECONOMY_VERBS.map((v, i) => (
                    <Reveal key={v.verb} delay={i * 0.06} className="bg-[#05070B]">
                        <div className="group h-full p-6 transition-colors duration-300 hover:bg-[#080F18]" data-testid={`verb-${v.verb.toLowerCase()}`}>
                            <div className="flex items-baseline justify-between">
                                <span className="font-mono text-lg tracking-[0.2em] text-[#F5F9FF] group-hover:text-[#00D9FF]">{v.verb}</span>
                                <span className="font-mono text-[9px] tracking-[0.2em] text-[#00D9FF]/50">{String(i + 1).padStart(2, "0")}</span>
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-[#B8C7D9]/85">{v.desc}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>

        <section className="border-t border-[rgba(0,217,255,0.1)] bg-[#080F18]/40 py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <SectionHead index="ECO.02" title="SIX THINGS MUST WORK TOGETHER." sub="Economic autonomy is a systems problem. Remove any one of these, and agentic commerce collapses back into trust-me software." />
                <div className="grid gap-5 md:grid-cols-2">
                    {PILLARS.map(([name, desc], i) => (
                        <Reveal key={name} delay={i * 0.06}>
                            <div className="flex gap-5 border-l border-[#00D9FF]/30 py-1 pl-6">
                                <div>
                                    <h3 className="font-mono text-sm tracking-[0.2em] text-[#F5F9FF]">{name}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#B8C7D9]">{desc}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <Panel title="SETTLEMENT CONTEXT" right={<Tag tone="live">OFFICIAL SOURCE</Tag>} className="mt-12" testid="economy-arc-panel">
                        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
                            <p className="max-w-2xl text-sm leading-relaxed text-[#B8C7D9] sm:text-base">
                                AgentVM is the runtime and verification layer. <span className="text-[#F5F9FF]">Arc is the economic layer</span> —
                                an open Layer-1 with USDC as native gas and deterministic settlement, built for the
                                agentic economy.
                            </p>
                            <div className="flex shrink-0 gap-3">
                                <Link to="/arc" data-testid="economy-arc-link" className="border border-[rgba(0,217,255,0.3)] px-4 py-2 font-mono text-[10px] tracking-[0.22em] text-[#B8C7D9] transition-colors hover:border-[#00D9FF]/70 hover:text-[#00D9FF]">
                                    EXPLORE ARC
                                </Link>
                                <a href={LINKS.arc} target="_blank" rel="noopener noreferrer" data-testid="economy-arc-official" className="border border-[#1688FF]/60 bg-[#006CFF]/20 px-4 py-2 font-mono text-[10px] tracking-[0.22em] text-[#F5F9FF] transition-colors hover:bg-[#006CFF]/40">
                                    ARC.IO ↗
                                </a>
                            </div>
                        </div>
                    </Panel>
                </Reveal>
            </div>
        </section>
    </div>
);

export default Economy;
